import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Server from "../../Shared/Server";
import Spinner from "../../Shared/Spinner";
import AsyncSelectField from "../../Shared/AsyncSelect";

const PermissionSetup = () => {
    const [allPermissions, setAllPermissions] = useState({});
    const [allPermissionLength, setAllPermissionLength] = useState([]);
    const [userPermissionLength, setUserPermissionLength] = useState([]);
    const [userPermissions, setUserPermissions] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [usersOptions, setUsersOptions] = useState([]);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({ user_id: 0 });
    const { user_id } = formData;
    const navigate = useNavigate();

    // Load users for Async Select
    const loadOptions = async (qry) => {
        try {
            const response = await Server.get(`/options/users?q=${qry}`);
            setUsersOptions(response.data);
            return response.data;
        } catch (error) {
            setLoading(false);
            return [];
        }
    };

    // Fetch user permissions
    useEffect(() => {
        if (user_id !== 0) {
            fetchUserPermissions(user_id);
        }
    }, [user_id]);

    const fetchUserPermissions = async (user_id) => {
        setLoading(true);
        try {
            const response = await Server.get(`/users/permissions?id=${user_id}`);
            const { all_permissions, user_permissions } = response.data;
            setAllPermissionLength(all_permissions)
            setUserPermissionLength(user_permissions);
            // Organize permissions by group
            const groupedPermissions = all_permissions.reduce((acc, perm) => {
                if (!acc[perm.group]) acc[perm.group] = [];
                acc[perm.group].push(perm);
                return acc;
            }, {});

            // Store user permissions as a Set for quick lookup
            const userPermSet = new Set(user_permissions.map((perm) => perm.id));

            setAllPermissions(groupedPermissions);
            setUserPermissions(userPermSet);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            if (error.response.status === 401) {
                navigate("/login");
            }else if (error.response.status === 403){
                toast.error(error.response.data.detail);
                navigate('/');
            }else {
                setErrors(error.response.data?.detail);
            }
        }
    };

    // Handle checkbox selection
    const handlePermissionChange = (id) => {
        setUserPermissions((prev) => {
            const newPermissions = new Set(prev);
            if (newPermissions.has(id)) {
                newPermissions.delete(id);
            } else {
                newPermissions.add(id);
            }
            return newPermissions;
        });
    };

    // Handle "Select All" per group
    const handleSelectAll = (group) => {
        setUserPermissions((prev) => {
            const newPermissions = new Set(prev);
            const groupPermissions = allPermissions[group].map((perm) => perm.id);
            const allSelected = groupPermissions.every((id) => newPermissions.has(id));

            if (allSelected) {
                groupPermissions.forEach((id) => newPermissions.delete(id));
            } else {
                groupPermissions.forEach((id) => newPermissions.add(id));
            }
            return newPermissions;
        });
    };

    // Handle form submission
    const handleSubmit = async () => {
        // console.log(Array.from(userPermissions));
        // return;
        try {
            setLoading(true);
            const response = await Server.post("/users/permissions/update", {
                user_id: user_id,
                permissions: Array.from(userPermissions),
            });
            setLoading(false);
            toast.success(response.data.detail);
        } catch (error) {
            setLoading(false);
            if (error.response.status === 401) {
                navigate("/login");
            }else if (error.response.status === 403){
                toast.error(error.response.data.detail);
                navigate('/');
            }else {
                setErrors(error.response.data?.detail);
            }
        }
    };

    // Handle user select change
    const onChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value,
        }));
    };

    if (loading) return <Spinner />;

    return (
        <div className="container mt-4">
            <h3 className="mb-4">User Permissions is {Object.keys(userPermissionLength).length} Menu(s) Out of {Object.keys(allPermissionLength).length} Menus</h3>
            <div className="row">
                <div className="col-md-4">
                    <div className="mb-3">
                        <AsyncSelectField
                            name="user_id"
                            label="Select User"
                            value={user_id}
                            onChange={onChange}
                            loadOptions={loadOptions}
                            options={usersOptions}
                            placeholder="Start typing to search..."
                            error={errors.user_id}
                        />
                    </div>
                </div>
                <div className="col-md-8"></div>
            </div>

            <div className="permissions-container">
                {Object.entries(allPermissions).map(([group, perms]) => (
                    <div key={group} className="mb-4">
                        <h5 className="fw-bold">{group}</h5>
                        <div className="form-check mb-2">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                onChange={() => handleSelectAll(group)}
                                checked={perms.every((p) => userPermissions.has(p.id))}
                            />
                            <label className="form-check-label">Select All</label>
                        </div>

                        <div className="row">
                            {perms.map(({ id, display_name, role }) => (
                                <div key={id} className="col-md-3">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            onChange={() => handlePermissionChange(id)}
                                            checked={userPermissions.has(id)}
                                        />
                                        <label className="form-check-label">{display_name} ~ {role}</label>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <button onClick={handleSubmit} className="btn btn-primary mb-3" style={{float: 'right'}}>Update Permissions</button>
        </div>
    );
};

export default PermissionSetup;